import * as THREE from 'three';
import { PPCDataChunk } from './PPCChunk';
import { EnumDataFileType } from './PPCFile';
import { PPCPointCloudGeometryNode } from './PPCPointCloudGeometryNode';
import workerResponseQueue from './WorkerResponseQueue';
import { loaded_tiles_network, rendered_tiles } from '../../potreeState';

export class PPCDataTile {
  fromIndex: number;
  toIndex: number;
  dataChunk: PPCDataChunk;
  numPoints: number;
  node: PPCPointCloudGeometryNode;

  constructor(args: { fromIndex: number; toIndex: number; dataChunk: PPCDataChunk; node: PPCPointCloudGeometryNode }) {
    const { fromIndex, toIndex, dataChunk, node } = args;
    this.fromIndex = fromIndex;
    this.toIndex = toIndex;
    this.dataChunk = dataChunk;
    this.numPoints = this.toIndex + 1 - this.fromIndex;
    this.node = node;
  }

  /**
   * Load tile data. This does NOT block - geometry processing is queued and
   * happens during the render loop.
   *
   * `onComplete` fires EXACTLY ONCE on every path that does not throw:
   * `true` when the geometry was processed, `false` when the load was
   * abandoned (node disposed, or evicted by the LRU mid-flight). The owning
   * node keeps the global in-flight counter balanced on that contract — a
   * missed callback used to leak a loader slot for the rest of the session.
   */
  async load(onComplete?: (loaded: boolean) => void) {
    const loadStartTime = performance.now();

    // Early exit if node is already disposed
    if (this.node.isDisposed()) {
      onComplete?.(false);
      return;
    }

    // Track tile load start for profiling
    if (typeof window.PPCLoaderDebugWidget !== 'undefined') {
      window.PPCLoaderDebugWidget.trackTileLoadStart();
    }

    const tileId = `${this.dataChunk.id}_${this.fromIndex}_${this.toIndex}`;
    loaded_tiles_network[tileId] = false;
    rendered_tiles[tileId] = false;

    const supportedDataFileTypes = this.dataChunk.ppcGeometry.supportedFileTypes.filter((type) => Object.values(EnumDataFileType).includes(type as EnumDataFileType));

    let positions: number[] | false | null = null;
    let colors: number[] | false | null = null;
    let intensities: number[] | false | null = null;
    let classifications: number[] | false | null = null;
    let segments: number[] | false | null = null;
    let sources: number[] | false | null = null;

    // Capture node reference for closure - check disposed state inside callback
    const node = this.node;
    const dataChunk = this.dataChunk;
    const numPoints = this.numPoints;

    try {
      [positions, colors, intensities, classifications, segments, sources] = await Promise.all([
        this.dataChunk.loadData({ type: EnumDataFileType.XYZ }),
        supportedDataFileTypes.includes(EnumDataFileType.RGB) && this.dataChunk.loadData({ type: EnumDataFileType.RGB }),
        supportedDataFileTypes.includes(EnumDataFileType.INTENSITY) && this.dataChunk.loadData({ type: EnumDataFileType.INTENSITY }),
        supportedDataFileTypes.includes(EnumDataFileType.CLASSIFICATION) && this.dataChunk.loadData({ type: EnumDataFileType.CLASSIFICATION }),
        supportedDataFileTypes.includes(EnumDataFileType.SEGMENT) && this.dataChunk.loadData({ type: EnumDataFileType.SEGMENT }),
        supportedDataFileTypes.includes(EnumDataFileType.SOURCE) && this.dataChunk.loadData({ type: EnumDataFileType.SOURCE }),
      ]);

      // Check if node was disposed while we were fetching
      if (node.isDisposed()) {
        positions = null;
        colors = null;
        intensities = null;
        classifications = null;
        segments = null;
        sources = null;
        if (typeof window.PPCLoaderDebugWidget !== 'undefined') {
          window.PPCLoaderDebugWidget.trackTileLoadAbort();
        }
        onComplete?.(false);
        return;
      }

      // Queue the heavy geometry processing work to be done in the main render loop
      // This prevents frame spikes from synchronous array processing and GPU uploads
      // NOTE: We do NOT await this - it will be processed during the render loop's time budget
      let tileLoadTracked = true; // trackTileLoadStart() ran above; closed by end/abort below
      let completionSignaled = false;
      const processGeometry = (): void => {
        // CRITICAL: Check if node was disposed before processing geometry
        // This prevents setting attributes on disposed/null geometry
        // (`!node.geometry` = the LRU evicted the node while its data was
        // still in flight — unloadForLRU nulls the geometry.)
        if (node.isDisposed() || !node.geometry) {
          // Cleanup arrays and exit early
          positions = null;
          colors = null;
          intensities = null;
          classifications = null;
          segments = null;
          sources = null;
          if (typeof window.PPCLoaderDebugWidget !== 'undefined') {
            window.PPCLoaderDebugWidget.trackTileLoadAbort();
          }
          onComplete?.(false);
          return;
        }

        const timings: { op: string; ms: number }[] = [];
        const callbackStart = performance.now();

        let t0 = performance.now();
        this.loadXYZ(positions);
        timings.push({ op: 'loadXYZ', ms: performance.now() - t0 });

        t0 = performance.now();
        this.loadColors(colors);
        timings.push({ op: 'loadColors', ms: performance.now() - t0 });

        if (intensities) {
          t0 = performance.now();
          this.loadIntensities(intensities);
          timings.push({ op: 'loadIntensities', ms: performance.now() - t0 });
        }

        if (classifications) {
          t0 = performance.now();
          this.loadClassifications(classifications);
          timings.push({ op: 'loadClassifications', ms: performance.now() - t0 });
        }

        if (segments) {
          t0 = performance.now();
          this.loadSegments(segments);
          timings.push({ op: 'loadSegments', ms: performance.now() - t0 });
        }

        if (sources) {
          t0 = performance.now();
          this.loadSources(sources);
          timings.push({ op: 'loadSources', ms: performance.now() - t0 });
        }

        const callbackTotal = performance.now() - callbackStart;

        // Track tile operation timings in debug widget
        if (typeof window.PPCLoaderDebugWidget !== 'undefined') {
          window.PPCLoaderDebugWidget.trackTileOperations({
            numPoints: numPoints,
            totalMs: callbackTotal,
            timings,
            timestamp: Date.now(),
          });
        }

        // Null out arrays after processing to help GC
        positions = null;
        colors = null;
        intensities = null;
        classifications = null;
        segments = null;
        sources = null;

        loaded_tiles_network[tileId] = true;
        rendered_tiles[tileId] = true;

        // Track tile load end with timing
        const loadEndTime = performance.now();
        const loadDuration = loadEndTime - loadStartTime;
        if (typeof window.PPCLoaderDebugWidget !== 'undefined') {
          window.PPCLoaderDebugWidget.trackTileLoadEnd(loadDuration);
        }
        tileLoadTracked = false;

        // Clear the file cache after loading to free memory
        // Guard against disposed chunk
        if (dataChunk) {
          dataChunk.clearFileCache();
        }

        // Completion callback — `true` marks the node loaded; a node disposed
        // in the meantime still gets its callback (as `false`) so the owner's
        // in-flight accounting stays balanced.
        completionSignaled = true;
        onComplete?.(!node.isDisposed());
      };

      workerResponseQueue.enqueue(() => {
        try {
          processGeometry();
        } catch (error) {
          // The queue swallows job errors — without this the owner's in-flight
          // slot would leak. Release it, then let the queue log the error.
          positions = null;
          colors = null;
          intensities = null;
          classifications = null;
          segments = null;
          sources = null;
          if (tileLoadTracked && typeof window.PPCLoaderDebugWidget !== 'undefined') {
            window.PPCLoaderDebugWidget.trackTileLoadAbort();
          }
          if (!completionSignaled) {
            completionSignaled = true;
            onComplete?.(false);
          }
          throw error;
        }
      });
    } catch (error) {
      // Ensure cleanup on error
      positions = null;
      colors = null;
      intensities = null;
      classifications = null;
      segments = null;
      sources = null;
      throw error;
    }
  }

  private loadXYZ(xyzValues: number[] | false | null) {
    if (!xyzValues || !xyzValues.length) return;
    // Guard against disposed node
    if (!this.node.geometry) return;

    const positionBuffer = new ArrayBuffer(this.numPoints * 4 * 3);
    const positions = new Float32Array(positionBuffer);
    const indexBuffer = new ArrayBuffer(this.numPoints * 4);
    const indices = new Uint32Array(indexBuffer);
    const bounds = {
      min: [Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE],
      max: [-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE],
    };

    let j = 0;
    const scale = this.node.ppcGeometry.scale;
    const shift = this.node.boundingBox.min;
    for (let i = this.fromIndex; i <= this.toIndex; i++) {
      positions[3 * j] = xyzValues[3 * (i - 1)] / scale - shift.x;
      positions[3 * j + 1] = xyzValues[3 * (i - 1) + 1] / scale - shift.y;
      positions[3 * j + 2] = xyzValues[3 * (i - 1) + 2] / scale - shift.z;

      bounds.min[0] = Math.min(bounds.min[0], positions[3 * j] + shift.x);
      bounds.min[1] = Math.min(bounds.min[1], positions[3 * j + 1] + shift.y);
      bounds.min[2] = Math.min(bounds.min[2], positions[3 * j + 2] + shift.z);
      bounds.max[0] = Math.max(bounds.max[0], positions[3 * j] + shift.x);
      bounds.max[1] = Math.max(bounds.max[1], positions[3 * j + 1] + shift.y);
      bounds.max[2] = Math.max(bounds.max[2], positions[3 * j + 2] + shift.z);

      indices[j] = j;
      j++;
    }
    this.node.tightBoundingBox = new THREE.Box3(new THREE.Vector3().fromArray(bounds.min), new THREE.Vector3().fromArray(bounds.max));
    this.node.tightBoundingSphere = this.node.tightBoundingBox?.getBoundingSphere(new THREE.Sphere());
    this.node.geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positionBuffer), 3, true));
    this.node.geometry.setAttribute('indices', new THREE.BufferAttribute(new Uint8Array(indexBuffer), 4, true));
  }

  private loadColors(rgbValues: number[] | false | null) {
    if (!rgbValues || !rgbValues.length) return;
    // Guard against disposed node
    if (!this.node.geometry) return;

    const colorBuffer = new ArrayBuffer(this.numPoints * 4);
    const colors = new Uint8Array(colorBuffer);
    let j = 0;
    for (let i = this.fromIndex; i <= this.toIndex; i++) {
      colors[j] = rgbValues[3 * (i - 1)];
      colors[j + 1] = rgbValues[3 * (i - 1) + 1];
      colors[j + 2] = rgbValues[3 * (i - 1) + 2];
      j += 4;
    }

    this.node.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 4, true));
  }

  private loadIntensities(intensityValues: number[] | false | null) {
    if (!intensityValues || !intensityValues.length) return;
    // Guard against disposed node
    if (!this.node.geometry) return;

    const fieldInfo = this.node.ppcGeometry.ppchReader.mFieldInfo;
    if (!fieldInfo) return;

    const minIntensity = fieldInfo.mMinIntensity;
    const maxIntensity = fieldInfo.mMaxIntensity;
    const intensityBuffer = new ArrayBuffer(this.numPoints * 4);
    const intensities = new Float32Array(intensityBuffer);
    let j = 0;
    for (let i = this.fromIndex; i <= this.toIndex; i++) {
      // Intensities are parsed to cardinal8 when compressed (range 0-255). This formula put's it back to the range before
      intensities[j++] = Math.min(Math.max(((intensityValues[i - 1] - 0.5) * (maxIntensity - minIntensity)) / 255 + minIntensity, minIntensity), maxIntensity);
    }
    this.node.geometry.setAttribute('intensity', new THREE.BufferAttribute(intensities, 1));
  }

  private loadClassifications(classificationValues: number[] | false | null) {
    if (!classificationValues || !classificationValues.length) return;
    // Guard against disposed node
    if (!this.node.geometry) return;

    const classificationBuffer = new ArrayBuffer(this.numPoints);
    const classifications = new Uint8Array(classificationBuffer);
    let j = 0;
    for (let i = this.fromIndex; i <= this.toIndex; i++) {
      classifications[j++] = classificationValues[i - 1];
    }
    this.node.geometry.setAttribute('classification', new THREE.BufferAttribute(classifications, 1));
  }

  private loadSegments(segmentValues: number[] | false | null) {
    if (!segmentValues || !segmentValues.length) return;
    // Guard against disposed node
    if (!this.node.geometry) return;

    const segmentBuffer = new ArrayBuffer(this.numPoints * 4);
    const segments = new Float32Array(segmentBuffer);

    // Build segment cache while loading
    const segmentCache = new Map<number, number[]>();

    let j = 0;
    for (let i = this.fromIndex; i <= this.toIndex; i++) {
      const segmentId = segmentValues[i - 1];
      segments[j] = segmentId;

      // Build cache: map segment ID to point indices
      if (!segmentCache.has(segmentId)) {
        segmentCache.set(segmentId, []);
      }
      const segment = segmentCache.get(segmentId);
      if (segment) segment.push(j);

      j++;
    }

    this.node.geometry.setAttribute('segment', new THREE.BufferAttribute(segments, 1));

    // Store the cache in the node (only if node not disposed)
    if (!this.node.isDisposed()) {
      this.node.setSegmentCache(segmentCache);
    }
  }

  private loadSources(sourceValues: number[] | false | null) {
    if (!sourceValues || !sourceValues.length) return;
    // Guard against disposed node
    if (!this.node.geometry) return;

    const sourceBuffer = new ArrayBuffer(this.numPoints * 4);
    const sources = new Float32Array(sourceBuffer);
    let j = 0;
    for (let i = this.fromIndex; i <= this.toIndex; i++) {
      sources[j++] = sourceValues[i - 1];
    }
    this.node.geometry.setAttribute('source', new THREE.BufferAttribute(sources, 1));
  }

  loadDifferences() {
    // const differenceValues = this?.dataChunk.data.DIFFERENCE?.values || [];
    // if (!differenceValues.length) return;
    // const differenceBuffer = new ArrayBuffer(this.numPoints * 4);
    // const differences = new Float32Array(differenceBuffer);
    // let scale = this.node.ppcGeometry.scale;
    // for (let i = this.fromIndex; i <= this.toIndex; i++) {
    //   differences[i] = differenceValues[i - 1] / scale - this.node.ppcGeometry.minDifference;
    // }
    // const bufferAttribute = new THREE.BufferAttribute(
    //   new Float32Array(differenceBuffer),
    //   1,
    //   true
    // ) as THREE.BufferAttribute & { potree: { offset: number; scale: number; range: [number, number] } };
    // bufferAttribute.potree = {
    //   offset: 0,
    //   scale: 1,
    //   range: [this.node.ppcGeometry.minDifference, this.node.ppcGeometry.maxDifference],
    // };
    // this.node.geometry.setAttribute('difference', bufferAttribute);
  }
}
